import { useCallback, useRef, useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  AllCommunityModule,
  ModuleRegistry,
  ColDef,
  ICellRendererParams,
  IHeaderParams,
  CellClickedEvent,
  ValueGetterParams,
  GridApi,
  GridReadyEvent,
  TextEditorModule,
  LargeTextEditorModule,
  DateEditorModule,
  SelectEditorModule,
  NumberEditorModule,
} from "ag-grid-community";
import { Box, Flex } from "@radix-ui/themes";
import { IEntity } from "@/types/breeze/Entity";
import CustomPagination from "@/components/pagination/CustomPagination";
import { PaginatedResult } from "@/types/breeze/Any";
import {
  StandardFieldDisplayType,
  FileFieldDisplayType,
  RelationshipFieldDisplayType,
  RelationshipFieldDatabaseType,
} from "@/types/breeze/Field";
import { getCellEditor, getCellRenderer } from "./DataGrid.utils";

// Register AG Grid modules
ModuleRegistry.registerModules([
  AllCommunityModule,
  TextEditorModule,
  LargeTextEditorModule,
  DateEditorModule,
  SelectEditorModule,
  NumberEditorModule,
]);

// Wraps default sort/filter behaviour so headerComponent doesn't strip them.
function HeaderWithBadge({ label, badge, params }: { label: string; badge: React.ReactNode; params: IHeaderParams }) {
  const [sort, setSort] = useState<"asc" | "desc" | null>(params.column.getSort() ?? null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onSortChanged = () => setSort(params.column.getSort() ?? null);
    params.column.addEventListener("sortChanged", onSortChanged);
    return () => params.column.removeEventListener("sortChanged", onSortChanged);
  }, [params.column]);

  const sortIcon = sort === "asc" ? "▲" : sort === "desc" ? "▼" : "";

  return (
    <Flex align="center" gap="2" style={{ width: "100%" }}>
      <Flex
        align="center"
        gap="1"
        style={{ flex: 1, cursor: params.enableSorting ? "pointer" : "default", userSelect: "none" }}
        onClick={(e) => params.enableSorting && params.progressSort(e.shiftKey)}
      >
        <span>{label}</span>
        {sortIcon && <span style={{ fontSize: "0.75em" }}>{sortIcon}</span>}
        {badge}
      </Flex>
      <button
        ref={menuButtonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          params.showColumnMenu(menuButtonRef.current!);
        }}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 2,
          opacity: 0.6,
          display: "flex",
          alignItems: "center",
        }}
        aria-label="Column menu"
      >
        <span className="ag-icon ag-icon-menu" />
      </button>
    </Flex>
  );
}

// Filter type definitions
interface BaseFilter {
  filter: unknown;
  type: string;
}

interface SetFilter {
  values: unknown[];
}

interface CompoundFilter {
  condition1: BaseFilter;
  condition2?: BaseFilter;
  operator?: string;
}

type FilterValue =
  | BaseFilter
  | SetFilter
  | CompoundFilter
  | Record<string, unknown>;

export interface DataGridColumn<T = Record<string, unknown>> {
  field: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  flex?: number;
  pinned?: "left" | "right";
  sortable?: boolean;
  filter?: boolean;
  resizable?: boolean;
  checkboxSelection?: boolean;
  headerCheckboxSelection?: boolean;
  fieldType?: {
    database_type?: string;
    display_type?: string;
    relationshipMetadata?: {
      relationshipId: number;
      targetEntity: IEntity | null;
      targetNameField?: string;
    };
    selectOptions?: Array<{ value: string; label: string }>;
    isMultiSelect?: boolean;
    create_db_column?: boolean;
  };
  header: string;
  headerBadge?: React.ReactNode;
  cellRenderer?: (data: T) => React.ReactNode;
  valueGetter?: (data: T) => string;
}

export interface DataGridProps<T> {
  data: PaginatedResult<T> | undefined | null;
  columns: DataGridColumn<T>[];
  storageKey: string;
  isLoading?: boolean;
  currentPage: number;
  currentPageSize: number;
  rowSelection?: "single" | "multiple" | "none";
  height?: string;
  topContent?: React.ReactNode;
  actionsWidth?: number;
  enableInstantEdit?: boolean;
  showPagination?: boolean; // New prop to control pagination visibility
  pageSizeOptions?: number[];
  pinnedTopRows?: T[];
  pinnedBottomRows?: T[];
  // "normal" respects a bounded container and scrolls internally; "autoHeight" grows to fit rows.
  domLayout?: "autoHeight" | "normal";
  selectedRowIds?: (string | number)[];
  actions?: (rowData: T) => React.ReactNode;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
  onFilterChange?: (filterQuery: string) => void;
  onSortChange?: (sortBy: string, sortDirection: string) => void;
  onSelectionChanged?: (selectedRows: T[]) => void;
  onCellClicked?: (event: CellClickedEvent, data: T) => void;
  onGridReady?: (params: { api: GridApi }) => void;
  onInstantEditToggle?: (enabled: boolean) => void;
  onCellValueChanged?: (event: unknown, data: T) => void;
  onRelationshipEdit?: (
    fieldName: string,
    currentValue: unknown,
    targetEntity: IEntity | null,
    relationshipId: number,
    rowData: T,
  ) => void;
  onFileEdit?: (fieldName: string, currentValue: unknown, rowData: T) => void;
  onMultiSelectEdit?: (
    fieldName: string,
    currentValue: unknown,
    selectOptions: Array<{ value: string; label: string }>,
    rowData: T,
  ) => void;
}

const DataGrid = <T extends { id: number | string }>({
  data,
  columns,
  storageKey,
  isLoading = false,
  currentPage,
  currentPageSize,
  rowSelection = "multiple",
  height = "100%",
  topContent,
  actionsWidth = 120,
  enableInstantEdit = false,
  showPagination = true,
  pageSizeOptions = [25, 50, 100, 150, 200, 250],
  pinnedTopRows,
  pinnedBottomRows,
  domLayout = "autoHeight",
  selectedRowIds,
  actions,
  onPageChange,
  onPageSizeChange,
  onFilterChange,
  onSortChange,
  onSelectionChanged,
  onCellClicked,
  onGridReady,
  onCellValueChanged,
  onRelationshipEdit,
  onFileEdit,
  onMultiSelectEdit,
}: DataGridProps<T>): JSX.Element => {
  const gridRef = useRef<AgGridReact>(null);
  const [colDefs, setColDefs] = useState<ColDef[]>([]);

  const saveGridState = useCallback(() => {
    if (gridRef.current) {
      try {
        const columnState = gridRef.current.api.getColumnState();
        const filterModel = gridRef.current.api.getFilterModel();

        const gridSort = columnState
          .filter((col) => col.sort !== null && col.sort !== undefined)
          .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
          .map((col) => (col.sort === "desc" ? `-${col.colId}` : col.colId));

        const state = {
          columnState,
          filterModel,
          gridSort: gridSort.length > 0 ? gridSort : null,
          currentPage,
          currentPageSize,
          timestamp: Date.now(),
        };

        localStorage.setItem(`aggrid_${storageKey}`, JSON.stringify(state));
      } catch (error) {
        console.warn("Failed to save AgGrid state:", error);
      }
    }
  }, [storageKey, currentPage, currentPageSize]);

  // Configure row selection for AG Grid
  const rowSelectionConfig =
    rowSelection === "none" || enableInstantEdit
      ? undefined
      : {
          mode:
            rowSelection === "single"
              ? ("singleRow" as const)
              : ("multiRow" as const),
          checkboxes: true,
          headerCheckbox: rowSelection !== "single",
          enableClickSelection: true,
          enableSelectionWithoutKeys: true,
        };

const ActionsWrapper = (params: ICellRendererParams & { actionsFn: (data: any) => React.ReactNode }) => {
  return <>{params.actionsFn(params.data)}</>;
};

// Convert our column definitions to AG Grid column definitions
  useEffect(() => {
    const defaultColumnProps: ColDef = {
      autoHeight: false,
      cellClass: "cell-wrap-text",
      resizable: true,
      filter: true,
      floatingFilter: false,
      suppressMovable: false,
      wrapHeaderText: true,
      editable: false,
    };

    let columnDefs: ColDef[] = columns.map((column) => {
      const rendererDef = getCellRenderer(column);
      const editorDef = enableInstantEdit
        ? getCellEditor(column)
        : { editable: false };

      return {
        ...defaultColumnProps,
        ...rendererDef,
        ...editorDef,
        field: column.field,
        headerName: column.header,
        headerComponent: column.headerBadge != null
          ? (params: IHeaderParams) => (
              <HeaderWithBadge label={column.header} badge={column.headerBadge!} params={params} />
            )
          : undefined,
        cellRenderer: column.cellRenderer
          ? (params: ICellRendererParams) => column.cellRenderer!(params.data)
          : rendererDef.cellRenderer,
        valueGetter: column.valueGetter
          ? (params: ValueGetterParams) => column.valueGetter!(params.data)
          : undefined,
        width: column.width,
        minWidth: column.minWidth || 150,
        maxWidth: column.maxWidth,
        flex: column.flex !== undefined ? column.flex : 1,
        pinned: column.pinned,
        sortable: column.sortable ?? column.fieldType?.create_db_column !== false,
        filter: column.filter ?? column.fieldType?.create_db_column !== false,
        filterValueGetter: (params: ValueGetterParams) => {
          const field = column.field;
          const val = params.data?.[field];

          if (val === null || val === undefined) return "";

          // If we have an active filter, try to return a value that matches it
          // while respecting the actual row data's display value
          if (typeof val === "object") {
            const nameField =
              column.fieldType?.relationshipMetadata?.targetNameField;
            if (nameField && val[nameField] !== undefined)
              return val[nameField];

            // Common fallback name fields
            if ("__name" in val) return val.__name;
            if ("name" in val) return val.name;
            if ("title" in val) return val.title;
            if ("label" in val) return val.label;
            return JSON.stringify(val);
          }
          return val;
        },
        resizable: column.resizable !== false,
      };
    });

    // Add actions column if actions prop is provided
    if (actions) {
      columnDefs.push({
        field: "actions",
        headerName: "",
        cellRenderer: ActionsWrapper,
        cellRendererParams: { actionsFn: actions },
        width: actionsWidth,
        minWidth: actionsWidth,
        maxWidth: 200,
        flex: 0,
        pinned: "right",
        lockPosition: true,
        sortable: false,
        filter: false,
        resizable: false,
        suppressMovable: true,
        cellClass: "actions-cell",
      });
    }

    // Apply saved state to respect existing order and visibility
    try {
      const saved = localStorage.getItem(`aggrid_${storageKey}`);
      if (saved) {
        const state = JSON.parse(saved);
        if (state.columnState) {
          const stateMap = new Map(
            state.columnState.map((s: any) => [s.colId, s]),
          );

          // Reorder columns based on state safely
          const stateOrder = state.columnState.map((s: any) => s.colId);
          columnDefs.sort((a, b) => {
            const idA = a.field || a.colId || "";
            const idB = b.field || b.colId || "";
            const indexA = stateOrder.indexOf(idA);
            const indexB = stateOrder.indexOf(idB);

            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            return 0;
          });

          // Apply other state properties (visibility, width, pinned)
          columnDefs = columnDefs.map((def) => {
            const colId = (def.field || def.colId || "") as string;
            if (!colId) return def;

            const s = (stateMap as Map<string, any>).get(colId);
            if (s) {
              return {
                ...def,
                width: s.width ?? def.width,
                hide: s.hide ?? def.hide,
                pinned: s.pinned ?? def.pinned,
              };
            }
            return def;
          });
        }
      }
    } catch (error) {
      console.warn("Failed to apply saved column state:", error);
    }

    setColDefs(columnDefs);
  }, [columns, rowSelection, actions, enableInstantEdit, storageKey]);

  // Handle selection based on selectedRowIds prop
  useEffect(() => {
    if (!gridRef.current?.api || !selectedRowIds) return;

    // Use a timeout to ensure row data is fully rendered if needed,
    // though AG Grid usually handles it fine on data update.
    gridRef.current.api.forEachNode((node) => {
      if (node.data && selectedRowIds.includes(node.data.id)) {
        node.setSelected(true);
      } else {
        node.setSelected(false);
      }
    });
  }, [data?.items, selectedRowIds, gridRef.current]);

  const handleFilterChange = useCallback(() => {
    if (!gridRef.current?.api || !onFilterChange) return;

    const filterModel = gridRef.current.api.getFilterModel();
    const queryParams = [];

    for (const [field, filter] of Object.entries(filterModel)) {
      if (filter && typeof filter === "object") {
        const filterValue = filter as FilterValue;
        let extractedFilterValue: unknown, type: string | undefined;

        const column = columns.find((col) => col.field === field);
        let filterField = field;

        if (
          column?.fieldType?.database_type ===
            RelationshipFieldDatabaseType.MANY_TO_ONE &&
          column?.fieldType?.relationshipMetadata?.targetEntity
        ) {
          const targetEntity =
            column.fieldType.relationshipMetadata.targetEntity;
          const nameField = targetEntity.fields?.find((f) => f.is_name_field);
          if (nameField) {
            filterField = `${field}.${nameField.name}`;
          }
        }

        // Handle different filter structures using proper type guards
        if ("filter" in filterValue && "type" in filterValue) {
          const baseFilter = filterValue as BaseFilter;
          extractedFilterValue = baseFilter.filter;
          type = baseFilter.type;
        } else if ("condition1" in filterValue) {
          const compoundFilter = filterValue as CompoundFilter;
          if (
            compoundFilter.condition1 &&
            "filter" in compoundFilter.condition1 &&
            "type" in compoundFilter.condition1
          ) {
            extractedFilterValue = compoundFilter.condition1.filter;
            type = compoundFilter.condition1.type;
          }
        } else if ("values" in filterValue) {
          const setFilter = filterValue as SetFilter;
          if (setFilter.values && setFilter.values.length > 0) {
            queryParams.push(
              `${filterField}=IN(${setFilter.values.join(",")})`,
            );
            continue;
          }
        }

        if (
          extractedFilterValue !== null &&
          extractedFilterValue !== undefined &&
          type
        ) {
          switch (type) {
            case "equals":
              queryParams.push(`${filterField}=${extractedFilterValue}`);
              break;
            case "notEqual":
              queryParams.push(`${filterField}=!${extractedFilterValue}`);
              break;
            case "contains":
              queryParams.push(`${filterField}=C:${extractedFilterValue}`);
              break;
            case "notContains":
              queryParams.push(`${filterField}=NC:${extractedFilterValue}`);
              break;
            case "startsWith":
              queryParams.push(`${filterField}=SW:${extractedFilterValue}`);
              break;
            case "endsWith":
              queryParams.push(`${filterField}=EW:${extractedFilterValue}`);
              break;
            case "greaterThan":
              queryParams.push(`${filterField}=GT:${extractedFilterValue}`);
              break;
            case "lessThan":
              queryParams.push(`${filterField}=LT:${extractedFilterValue}`);
              break;
            default:
              break;
          }
        }
      }
    }

    const queryString = queryParams.join("&");
    onFilterChange(queryString);
    saveGridState();
  }, [onFilterChange, saveGridState]);

  const handleSortChange = useCallback(() => {
    if (!gridRef.current?.api || !onSortChange) return;

    const columnState = gridRef.current.api.getColumnState();
    const activeSort = columnState.find((state) => state.sort);

    if (activeSort) {
      onSortChange(activeSort.colId, activeSort.sort || "");
    } else {
      onSortChange("", "");
    }
    saveGridState();
  }, [onSortChange, saveGridState]);

  const handleCellClicked = (event: CellClickedEvent) => {
    // Detect checkbox selection column - AG Grid uses 'ag-Grid-SelectionColumn' for checkbox columns
    const colId = event.column.getColId();
    const isCheckboxColumn =
      colId === "ag-Grid-SelectionColumn" ||
      colId.includes("ag-Grid-SelectionColumn");

    const isActionColumn = event.colDef.field === "actions";

    // Don't trigger navigation for checkbox or action columns
    if (isCheckboxColumn || isActionColumn) {
      return;
    }

    // Check for special field clicks in edit mode
    if (enableInstantEdit) {
      const clickedColumn = columns.find(
        (col) => col.field === event.colDef.field,
      );
      if (clickedColumn) {
        const displayType = clickedColumn.fieldType?.display_type;

        switch (displayType) {
          case RelationshipFieldDisplayType.RELATIONSHIP:
            if (onRelationshipEdit) {
              const fieldName = event.colDef.field || "";
              const currentValue = event.data[fieldName];
              const targetEntity =
                clickedColumn.fieldType?.relationshipMetadata?.targetEntity ||
                null;
              const relationshipId =
                clickedColumn.fieldType?.relationshipMetadata?.relationshipId ||
                0;
              onRelationshipEdit(
                fieldName,
                currentValue,
                targetEntity,
                relationshipId,
                event.data,
              );
            }
            return;

          case FileFieldDisplayType.FILE:
            if (onFileEdit) {
              const fieldName = event.colDef.field || "";
              const currentValue = event.data[fieldName];
              onFileEdit(fieldName, currentValue, event.data);
            }
            return;

          case StandardFieldDisplayType.SELECT:
            if (clickedColumn.fieldType?.isMultiSelect) {
              if (onMultiSelectEdit) {
                const fieldName = event.colDef.field || "";
                const currentValue = event.data[fieldName];
                const selectOptions =
                  clickedColumn.fieldType?.selectOptions || [];
                onMultiSelectEdit(
                  fieldName,
                  currentValue,
                  selectOptions,
                  event.data,
                );
              }
              return;
            }
            break;

          default:
            break;
        }
      }
    }

    // In navigation mode, trigger navigation for data cells
    if (!enableInstantEdit && onCellClicked) {
      onCellClicked(event, event.data);
    }

    // In instant edit mode, AG Grid will handle cell editing automatically
    // due to singleClickEdit=true and the editable column configuration
  };

  const handleSelectionChanged = useCallback(() => {
    if (!gridRef.current?.api || !onSelectionChanged) return;
    const selectedRows = gridRef.current.api.getSelectedRows();
    onSelectionChanged(selectedRows);
  }, [onSelectionChanged]);

  const handleCellValueChanged = useCallback(
    (event: unknown) => {
      const cellEvent = event as {
        colDef?: { field: string };
        oldValue: unknown;
        newValue: unknown;
        data: T;
      };
      if (!onCellValueChanged) {
        return;
      }

      onCellValueChanged(event, cellEvent.data);
    },
    [onCellValueChanged],
  );

  const handleGridReady = useCallback(
    (params: GridReadyEvent<T>) => {
      onGridReady?.({ api: params.api });
    },
    [onGridReady],
  );

  return (
    <Flex direction="column" height={height} style={{ overflow: "hidden" }}>
      {/* Top content and pagination */}
      {(topContent || showPagination) && (
        <Flex
          justify="between"
          align="center"
          px="0"
          py="2"
          style={{
            borderBottom: "1px solid var(--gray-6)",
          }}
        >
          <Box flexShrink="0">{topContent}</Box>
          {showPagination && (
            <CustomPagination
              currentPage={currentPage}
              totalPages={data?.total_pages ?? 1}
              pageSize={currentPageSize}
              totalItems={data?.total_items ?? 0}
              pageSizeOptions={pageSizeOptions}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          )}
        </Flex>
      )}

      {/* AG Grid */}
      <Box flexGrow="1" width="100%" minHeight="0">
        <div
          className="ag-theme-alpine"
          style={{
            width: "100%",
            height: domLayout === "normal" ? "100%" : undefined,
          }}
        >
          <AgGridReact
            ref={gridRef}
            columnDefs={colDefs}
            rowData={data?.items}
            pinnedTopRowData={pinnedTopRows}
            pinnedBottomRowData={pinnedBottomRows}
            suppressPaginationPanel={true}
            loading={isLoading}
            domLayout={domLayout}
            suppressHorizontalScroll={false}
            alwaysShowHorizontalScroll={false}
            suppressScrollOnNewData={true}
            rowSelection={rowSelectionConfig}
            enableCellTextSelection={!enableInstantEdit}
            singleClickEdit={enableInstantEdit}
            stopEditingWhenCellsLoseFocus={true}
            suppressClickEdit={!enableInstantEdit}
            floatingFiltersHeight={0}
            enableBrowserTooltips={true}
            onFilterChanged={handleFilterChange}
            onSortChanged={handleSortChange}
            onSelectionChanged={handleSelectionChanged}
            onCellClicked={handleCellClicked}
            onCellValueChanged={handleCellValueChanged}
            onColumnMoved={saveGridState}
            onColumnVisible={saveGridState}
            onColumnResized={saveGridState}
            onColumnPinned={saveGridState}
            onGridReady={handleGridReady}
          />
        </div>
      </Box>
    </Flex>
  );
};

export default DataGrid;
